/**
 * The session: who is signed in and the tokens proving it.
 *
 * This slice holds only what cannot be derived from a server response. The
 * user's profile, their rides, their vehicles and everything else are RTK Query
 * cache entries, not slice state: duplicating a server resource into a slice
 * is how the MobX version ended up with three copies of the profile that could
 * disagree with each other.
 */

import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { AuthResponse, TokenPair, User } from "../../api/types";
import tokenStorage from "../../lib/tokenStorage";

export interface AuthState {
    accessToken: string | null;
    refreshToken: string | null;
    /** RFC3339. Compared against the clock to pre-empt a doomed request. */
    expiresAt: string | null;
    user: User | null;
    /**
     * True once the stored session has been read. Guards render: without it
     * every protected route flashes the login screen on reload.
     */
    hydrated: boolean;
}

function initialState(): AuthState {
    tokenStorage.clearLegacy();
    const stored = tokenStorage.load();
    return {
        accessToken: stored?.accessToken ?? null,
        refreshToken: stored?.refreshToken ?? null,
        expiresAt: stored?.expiresAt ?? null,
        user: stored?.user ?? null,
        hydrated: true,
    };
}

const authSlice = createSlice({
    name: "auth",
    initialState: initialState(),
    reducers: {
        /** A completed sign-in or registration: tokens and the account together. */
        sessionEstablished(state, action: PayloadAction<AuthResponse>) {
            const { user, tokens } = action.payload;
            state.accessToken = tokens.accessToken;
            state.refreshToken = tokens.refreshToken;
            state.expiresAt = tokens.expiresAt;
            state.user = user;
            tokenStorage.saveTokens(tokens);
            tokenStorage.saveUser(user);
        },

        /** A refresh rotated the pair. The account is unchanged. */
        tokensRefreshed(state, action: PayloadAction<TokenPair>) {
            state.accessToken = action.payload.accessToken;
            state.refreshToken = action.payload.refreshToken;
            state.expiresAt = action.payload.expiresAt;
            tokenStorage.saveTokens(action.payload);
        },

        /** The profile changed under us: /users/me, or a successful update. */
        userLoaded(state, action: PayloadAction<User>) {
            state.user = action.payload;
            tokenStorage.saveUser(action.payload);
        },

        /**
         * End of session, for any reason: an explicit sign-out, a refusal the
         * refresh token could not fix, or a deleted account. Clearing the
         * RTK Query cache is the caller's job (see signOut in authActions).
         */
        sessionCleared(state) {
            state.accessToken = null;
            state.refreshToken = null;
            state.expiresAt = null;
            state.user = null;
            tokenStorage.clear();
        },
    },
});

export const {
    sessionEstablished,
    tokensRefreshed,
    userLoaded,
    sessionCleared,
} = authSlice.actions;

export default authSlice.reducer;
